import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import type { AuthSession } from "@/pages/login/domain/entities/auth"
import { fetchCurrentUser } from "./current-user"
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from "./session-storage"

export type AuthStatus = "loading" | "authenticated" | "unauthenticated"

type AuthContextValue = {
  status: AuthStatus
  session: AuthSession | null
  setSession: (next: AuthSession | null) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>(() => {
    const s = readStoredSession()
    if (!s?.accessToken || !s?.refreshToken) return "unauthenticated"
    return "loading"
  })
  const [session, setSessionState] = useState<AuthSession | null>(null)

  const verifyOrClear = useCallback(async () => {
    const from = readStoredSession()
    if (!from?.accessToken || !from?.refreshToken) {
      setStatus("unauthenticated")
      setSessionState(null)
      return false
    }
    setStatus("loading")
    setSessionState(null)
    try {
      const me = await fetchCurrentUser()
      if (!me.is_active) {
        throw new Error("User is inactive")
      }
      const full: AuthSession = {
        accessToken: from.accessToken,
        refreshToken: from.refreshToken,
        email: me.email,
        userId: me.id,
      }
      setSessionState(full)
      writeStoredSession(full)
      setStatus("authenticated")
      return true
    } catch {
      clearStoredSession()
      setSessionState(null)
      setStatus("unauthenticated")
      return false
    }
  }, [])

  useEffect(() => {
    void verifyOrClear()
  }, [verifyOrClear])

  const setSession = useCallback(
    async (next: AuthSession | null) => {
      if (!next) {
        clearStoredSession()
        setSessionState(null)
        setStatus("unauthenticated")
        return
      }
      writeStoredSession(next)
      const ok = await verifyOrClear()
      if (!ok) {
        throw new Error("Session could not be verified")
      }
    },
    [verifyOrClear]
  )

  const logout = useCallback(() => {
    clearStoredSession()
    setSessionState(null)
    setStatus("unauthenticated")
  }, [])

  const value = useMemo(
    () => ({ status, session, setSession, logout }),
    [status, session, setSession, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
